import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Row, Col, Modal, Form } from 'react-bootstrap';
import { ShoppingCartOutlined, DownloadOutlined } from '@ant-design/icons';
import { ApprovalApplicationExtended } from '../types/approval';
import { LimsNavIcon } from './LimsNavIcon';
import { StorageLocationModal } from './StorageLocationModal';
import { GoPurchaseFromRestockModal } from './GoPurchaseFromRestockModal';
import { ManualClearRestockModal } from './ManualClearRestockModal';
import {
  batchUpdateProcurementStatus,
  confirmPlatformOrderCompleted,
  getProcurementStatusMap,
  stockInConsumableFromApproval,
  updateConsumableProject as updateConsumableProjectApi,
  updateProcurementStatus as updateProcurementStatusApi
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_CONSUMABLE_IMAGE } from '../constants/defaultImages';
import { downloadProcurementExportZip } from '../utils/downloadProcurementExportZip';
import { ProcurementPrefillState } from '../types/procurementPrefill';
import { getConsumableBrandOptions } from '../constants/consumableBrands';
import AfterSalesEntry from './AfterSalesEntry';
import { listTeamAfterSales } from '../services/afterSalesService';
import { AfterSalesRequest } from '../types/afterSales';
import '../pages/MemberManagement.css';
import '../styles/resourceManagement.css';

interface ConsumableProcurementProps {
  applications: ApprovalApplicationExtended[];
  /** 管理员 / 耗材管理员 / 配置的耗材审批人：可操作；普通成员只读本人数据 */
  canManage?: boolean;
  /** 编辑采购信息后刷新申请列表 */
  onRefresh?: () => void;
}

interface ConsumableProjectEditForm {
  category: string;
  name: string;
  model: string;
  brand: string;
  unit: string;
  purchaseChannel: string;
  purchaseLink: string;
  supplier: string;
  amount: string;
  price: string;
  usage: string;
}

const resolveUploadedImageUrl = (url?: string) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/uploads/')) return `/api${url}`;
  return url;
};

const isMerchantPurchase = (row: {
  applyChannel?: string;
  merchantOrder?: { applyChannel?: string } | null;
}) =>
  row.applyChannel === 'merchant' || row.merchantOrder?.applyChannel === 'merchant';

const formatMerchantTracking = (merchantOrder?: ApprovalApplicationExtended['merchantOrder'] | null) => {
  const list = merchantOrder?.trackingNumbers || [];
  if (!list.length) return '';
  return list
    .map((t) => {
      const n = String(t.number || '').trim();
      const note = String(t.note || '').trim();
      return note ? `${n}（${note}）` : n;
    })
    .filter(Boolean)
    .join('、');
};

const formatLinePurchaseTotal = (item: {
  price?: string | number;
  amount?: string | number;
  total?: string | number;
  projectIndex?: number;
  merchantOrder?: {
    lineQuotes?: Array<{ projectIndex?: number; lineAmount?: number | null }>;
  } | null;
}) => {
  const quotes = item.merchantOrder?.lineQuotes;
  if (Array.isArray(quotes) && item.projectIndex != null) {
    const hit = quotes.find((q) => Number(q.projectIndex) === Number(item.projectIndex));
    if (hit && hit.lineAmount != null && Number.isFinite(Number(hit.lineAmount))) {
      return `¥${Number(hit.lineAmount).toFixed(2)}`;
    }
  }
  const total = Number(item.total);
  if (Number.isFinite(total) && String(item.total ?? '').trim() !== '') {
    return `¥${total.toFixed(2)}`;
  }
  const qty = Number(item.amount);
  const unit = Number(item.price);
  if (Number.isFinite(qty) && Number.isFinite(unit) && qty >= 0 && unit >= 0) {
    return `¥${(qty * unit).toFixed(2)}`;
  }
  return '—';
};

const ConsumableProcurement: React.FC<ConsumableProcurementProps> = ({ applications, canManage = true, onRefresh }) => {
  const { user } = useAuth();
  const currentUserId = user?._id || user?.id || localStorage.getItem('userId') || '';

  const [procurementStatus, setProcurementStatus] = useState<Record<string, string>>({});
  const [serverStorageLocations, setServerStorageLocations] = useState<Record<string, Record<string, string>>>({});
  const [serverReceiptPhotos, setServerReceiptPhotos] = useState<Record<string, Record<string, string[]>>>({});
  const [selectedApplication, setSelectedApplication] = useState<ApprovalApplicationExtended | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPickupModal, setShowPickupModal] = useState(false);
  const [selectedConsumable, setSelectedConsumable] = useState<any>(null);
  const [pickupInfo, setPickupInfo] = useState({
    pickupLocation: '',
    pickupNumber: '',
    needsPickup: true
  });
  const [storageLocation, setStorageLocation] = useState<Record<string, string>>({});
  const [showStorageLocationModal, setShowStorageLocationModal] = useState(false);
  const [pendingStorageRowKey, setPendingStorageRowKey] = useState<string | null>(null);
  const [pendingStorageApplicantId, setPendingStorageApplicantId] = useState<string>('');
  const [pendingStorageItemLabel, setPendingStorageItemLabel] = useState('');

  // 导出功能相关状态
  const [statusFilter, setStatusFilter] = useState<string>('全部');
  const [categoryFilter, setCategoryFilter] = useState<string>('全部');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false);
  const [exportedItems, setExportedItems] = useState<any[]>([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editApplicationKey, setEditApplicationKey] = useState<string>('');
  const [editProjectIndex, setEditProjectIndex] = useState<number>(-1);
  const [editForm, setEditForm] = useState<ConsumableProjectEditForm | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [goPurchaseShow, setGoPurchaseShow] = useState(false);
  const [goPurchasePrefill, setGoPurchasePrefill] = useState<Omit<
    ProcurementPrefillState,
    'channel' | 'merchantId' | 'merchantName'
  > | null>(null);
  const [goPurchaseLabel, setGoPurchaseLabel] = useState('');
  const [manualClearShow, setManualClearShow] = useState(false);
  const [manualClearRow, setManualClearRow] = useState<any>(null);
  const [manualClearConfirming, setManualClearConfirming] = useState(false);
  const [afterSalesRequests, setAfterSalesRequests] = useState<AfterSalesRequest[]>([]);
  const [projectStatusMap, setProjectStatusMap] = useState<Record<string, Record<string, string>>>({});

  const loadAfterSales = async () => {
    try {
      const response = await listTeamAfterSales('耗材申请');
      setAfterSalesRequests(response.data || []);
    } catch {
      setAfterSalesRequests([]);
    }
  };

  const openGoPurchase = (consumable: any) => {
    const name = String(consumable.name || '耗材');
    setGoPurchaseLabel(name);
    setGoPurchasePrefill({
      category: '耗材申请',
      sourceSuggestionApplicationId: String(consumable.applicationKey || ''),
      title: `补货：${name}`,
      reason: `低库存自动提醒跟进采购。${String(consumable.reason || '').trim()}`.trim(),
      priority: (consumable.priority as ProcurementPrefillState['priority']) || '普通',
      consumableProject: {
        category: consumable.category === '公共耗材' ? '公共耗材' : '小组耗材',
        image: consumable.image || '',
        name,
        model: String(consumable.model || ''),
        brand: String(consumable.brand || ''),
        unit: String(consumable.unit || ''),
        purchaseChannel: '网购',
        purchaseLink: String(consumable.purchaseLink || ''),
        supplier: String(consumable.supplier || ''),
        amount: consumable.amount != null ? String(consumable.amount) : '',
        price: consumable.price != null ? String(consumable.price) : '',
        usage: String(consumable.usage || '')
      }
    });
    setGoPurchaseShow(true);
  };

  const openEditProject = (consumable: any) => {
    setEditApplicationKey(String(consumable.applicationKey || ''));
    setEditProjectIndex(Number(consumable.projectIndex));
    setEditForm({
      category:
        consumable.category === '公共耗材' || consumable.category === '小组耗材'
          ? consumable.category
          : '小组耗材',
      name: String(consumable.name || ''),
      model: String(consumable.model || ''),
      brand: String(consumable.brand || ''),
      unit: String(consumable.unit || ''),
      purchaseChannel: String(consumable.purchaseChannel || ''),
      purchaseLink: String(consumable.purchaseLink || ''),
      supplier: String(consumable.supplier || ''),
      amount: consumable.amount != null ? String(consumable.amount) : '',
      price: consumable.price != null ? String(consumable.price) : '',
      usage: String(consumable.usage || '')
    });
    setShowEditModal(true);
  };

  const handleSaveEditProject = async () => {
    if (!editForm || !editApplicationKey || editProjectIndex < 0) return;
    if (!editForm.name.trim()) {
      alert('耗材名称不能为空');
      return;
    }
    setEditSaving(true);
    try {
      const response = await updateConsumableProjectApi(editApplicationKey, editProjectIndex, {
        category: editForm.category === '公共耗材' ? '公共耗材' : '小组耗材',
        name: editForm.name.trim(),
        model: editForm.model.trim(),
        brand: editForm.brand.trim(),
        unit: editForm.unit.trim(),
        purchaseChannel: editForm.purchaseChannel.trim(),
        purchaseLink: editForm.purchaseLink.trim(),
        supplier: editForm.supplier.trim(),
        amount: editForm.amount.trim(),
        price: editForm.price.trim(),
        usage: editForm.usage.trim()
      });
      if (!response.success) {
        alert(response.error || '保存失败');
        return;
      }
      setShowEditModal(false);
      setEditForm(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : '保存失败');
    } finally {
      setEditSaving(false);
    }
  };

  const handleConfirmPlatformOrder = async (
    applicationId: string,
    options?: { isMerchant?: boolean }
  ) => {
    const isMerchant = Boolean(options?.isMerchant);
    const message = isMerchant
      ? '确认勾选「已完成平台下单」？勾选后状态将同步到商家端，商家将无法再修改下单备注。'
      : '确认勾选「已完成平台下单」？此为内部采购信息记录，不会通知商家。';
    if (!window.confirm(message)) return;
    const response = await confirmPlatformOrderCompleted(applicationId);
    if (!response.success) {
      alert(response.error || '勾选失败');
      return;
    }
    if (onRefresh) onRefresh();
  };

  useEffect(() => {
    const loadStatusMap = async () => {
      try {
        const response = await getProcurementStatusMap('耗材申请');
        if (response.success && response.data) {
          const statusMap = Object.keys(response.data).reduce((acc, applicationId) => {
            acc[applicationId] = response.data![applicationId]?.status || '待采购';
            return acc;
          }, {} as Record<string, string>);
          const projectMap = Object.keys(response.data).reduce((acc, applicationId) => {
            acc[applicationId] = response.data![applicationId]?.projectStatuses || {};
            return acc;
          }, {} as Record<string, Record<string, string>>);
          const locationMap = Object.keys(response.data).reduce((acc, applicationId) => {
            acc[applicationId] = response.data![applicationId]?.storageLocations || {};
            return acc;
          }, {} as Record<string, Record<string, string>>);
          const photoMap = Object.keys(response.data).reduce((acc, applicationId) => {
            acc[applicationId] = response.data![applicationId]?.receiptPhotos || {};
            return acc;
          }, {} as Record<string, Record<string, string[]>>);
          setProcurementStatus(statusMap);
          setProjectStatusMap(projectMap);
          setServerStorageLocations(locationMap);
          setServerReceiptPhotos(photoMap);
        } else {
          setProcurementStatus({});
          setProjectStatusMap({});
          setServerStorageLocations({});
          setServerReceiptPhotos({});
        }
      } catch {
        setProcurementStatus({});
        setProjectStatusMap({});
        setServerStorageLocations({});
        setServerReceiptPhotos({});
      }
    };
    loadStatusMap();
    void loadAfterSales();
  }, [applications]);

  // 导出功能相关函数
  const handleSelectItem = (itemKey: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(itemKey);
    } else {
      newSelected.delete(itemKey);
    }
    setSelectedItems(newSelected);
  };

  const getProjectDisplayStatus = (applicationKey: string, projectIndex: number) => {
    const byProject = projectStatusMap[applicationKey]?.[String(projectIndex)];
    if (byProject) return byProject;
    const appStatus = procurementStatus[applicationKey] || '待采购';
    if (appStatus !== '已入库') return appStatus;
    const localLocation = storageLocation[`${applicationKey}-${projectIndex}`];
    const savedLocation = serverStorageLocations[applicationKey]?.[String(projectIndex)];
    return localLocation || savedLocation ? '已入库' : '已到货';
  };

  const getFilteredItems = () => {
    const consumableApps = getConsumableApplications();
    const allItems = consumableApps.flatMap(app => 
      app.consumableProjects?.map((project, index) => ({
        ...project,
        category:
          String((project as { category?: string }).category || '').trim() === '公共耗材'
            ? '公共耗材'
            : String((project as { category?: string }).category || '').trim() === '小组耗材'
              ? '小组耗材'
              : '小组耗材',
        // 添加申请的基本信息
        title: app.title,
        reason: app.reason,
        priority: app.priority,
        applicationKey: app.key,
        applicant: app.applicant,
        applicantId: app.applicantId,
        autoRestock: Boolean((app as any).autoRestock),
        applyTime: app.applyTime,
        projectIndex: index,
        key: `${app.key}-${index}`,
        applyChannel: app.applyChannel || 'internal',
        merchantOrder: app.merchantOrder || null,
        platformOrderRecord: app.platformOrderRecord || null
      })) || []
    );

    let items = allItems;
    if (statusFilter !== '全部') {
      items = items.filter(
        (item) => getProjectDisplayStatus(item.applicationKey, item.projectIndex) === statusFilter
      );
    }
    if (categoryFilter !== '全部') {
      items = items.filter((item) => item.category === categoryFilter);
    }
    return items;
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(new Set(getFilteredItems().map((i) => i.key)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleExport = () => {
    const filteredItems = getFilteredItems();
    const itemsToExport = selectedItems.size > 0 
      ? filteredItems.filter(item => selectedItems.has(item.key))
      : filteredItems;

    if (itemsToExport.length === 0) {
      alert('没有可导出的数据');
      return;
    }

    setExportedItems(itemsToExport);
    setShowExportModal(true);
  };

  const exportToExcel = async () => {
    const itemsToExport = exportedItems;

    const headers = [
      '申请标题',
      '申请人',
      '申请时间',
      '申请原因',
      '优先级',
      '耗材名称',
      '型号',
      '品牌',
      '单位',
      '购买渠道',
      '购买链接',
      '供应商',
      '申请数量',
      '单价',
      '总价',
      '备注价格',
      '备注链接',
      '是否完成平台下单',
      '使用说明',
      '当前状态',
      '放置位置',
      '收货照片URL'
    ];

    try {
      await downloadProcurementExportZip({
        filePrefix: '耗材采购列表',
        csvHeaders: headers,
        rows: itemsToExport.map((item) => {
          const loc =
            storageLocation[item.key] ||
            serverStorageLocations[item.applicationKey]?.[String(item.projectIndex)] ||
            '';
          const photos =
            serverReceiptPhotos[item.applicationKey]?.[String(item.projectIndex)] || [];
          return {
            rowKey: `${item.applicant || '申请人'}_${item.name || item.key}_${item.projectIndex}`,
            receiptPhotoUrls: photos,
            csvCells: [
              `"${item.title || ''}"`,
              `"${item.applicant || ''}"`,
              `"${new Date(item.applyTime).toLocaleDateString()}"`,
              `"${item.reason || ''}"`,
              `"${item.priority || ''}"`,
              `"${item.name || ''}"`,
              `"${item.model || ''}"`,
              `"${item.brand || ''}"`,
              `"${item.unit || ''}"`,
              `"${item.purchaseChannel || ''}"`,
              `"${item.purchaseLink || ''}"`,
              `"${item.supplier || ''}"`,
              `"${item.amount || ''}"`,
              `"${item.price || ''}"`,
              `"${item.total || ''}"`,
              `"${
                item.merchantOrder?.platformOrderNote?.submittedAt
                  ? item.merchantOrder.platformOrderNote.price ?? ''
                  : ''
              }"`,
              `"${
                item.merchantOrder?.platformOrderNote?.submittedAt
                  ? item.merchantOrder.platformOrderNote.link || ''
                  : ''
              }"`,
              `"${
                item.merchantOrder?.platformOrderNote?.completed ||
                item.platformOrderRecord?.completed
                  ? '是'
                  : '否'
              }"`,
              `"${item.usage || ''}"`,
              `"${getProjectDisplayStatus(item.applicationKey, item.projectIndex)}"`,
              `"${loc}"`,
              `"${photos.join(' | ')}"`
            ]
          };
        })
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : '导出失败');
      return;
    }

    setShowExportModal(false);

    const pendingItems = itemsToExport.filter(
      (item) =>
        getProjectDisplayStatus(item.applicationKey, item.projectIndex) === '待采购' &&
        !isMerchantPurchase(item)
    );

    if (pendingItems.length > 0) {
      setExportedItems(pendingItems);
      setShowStatusUpdateModal(true);
    }
  };

  const handleStatusUpdate = async () => {
    const ids = Array.from(new Set(exportedItems.map((item) => item.applicationKey).filter(Boolean)));
    if (ids.length === 0) {
      setShowStatusUpdateModal(false);
      return;
    }
    const response = await batchUpdateProcurementStatus({
      category: '耗材申请',
      applicationIds: ids,
      status: '已采购'
    });
    if (!response.success) {
      alert(response.error || '批量更新失败');
      return;
    }
    ids.forEach((id) => {
      setProcurementStatus((prev) => ({ ...prev, [id]: '已采购' }));
      const touched = exportedItems.filter((item) => item.applicationKey === id);
      setProjectStatusMap((prev) => {
        const nextForApp = { ...(prev[id] || {}) };
        touched.forEach((item) => {
          nextForApp[String(item.projectIndex)] = '已采购';
        });
        return { ...prev, [id]: nextForApp };
      });
    });
    setShowStatusUpdateModal(false);
    setSelectedItems(new Set());
    alert(`已将${exportedItems.length}个耗材状态更新为"已采购"`);
  };

  // 获取已通过的耗材申请
  const getConsumableApplications = () => {
    return applications.filter(app => 
      app.category === '耗材申请' && app.status === '已通过'
    );
  };

  const categoryOptions = useMemo(() => {
    const apps = applications.filter(
      (app) => app.category === '耗材申请' && app.status === '已通过'
    );
    const set = new Set<string>();
    apps.forEach((app) => {
      (app.consumableProjects || []).forEach((p) => {
        const raw = String((p as { category?: string }).category || '').trim();
        set.add(raw === '公共耗材' ? '公共耗材' : '小组耗材');
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'zh-CN'));
  }, [applications]);

  useEffect(() => {
    setCategoryFilter((prev) =>
      prev !== '全部' && !categoryOptions.includes(prev) ? '全部' : prev
    );
  }, [categoryOptions]);

  // 更新采购状态
  const updateProcurementStatus = async (
    applicationId: string,
    status: '待采购' | '已采购' | '已到货' | '已入库' | '已处理',
    options?: {
      pickupInfo?: { needsPickup: boolean; pickupLocation: string; pickupNumber: string };
      projectIndex?: number;
      storageLocation?: string;
      receiptPhotoUrls?: string[];
      clearReminder?: boolean;
    }
  ) => {
    const response = await updateProcurementStatusApi(applicationId, {
      category: '耗材申请',
      status,
      ...(options || {})
    });
    if (!response.success) {
      alert(response.error || '状态更新失败');
      return false;
    }
    setProcurementStatus((prev) => ({
      ...prev,
      [applicationId]: response.data?.status || status
    }));
    if (response.data?.projectStatuses) {
      setProjectStatusMap((prev) => ({
        ...prev,
        [applicationId]: response.data?.projectStatuses || {}
      }));
    } else if (options?.projectIndex != null) {
      setProjectStatusMap((prev) => ({
        ...prev,
        [applicationId]: {
          ...(prev[applicationId] || {}),
          [String(options.projectIndex)]: status
        }
      }));
    }
    if (response.data?.storageLocations) {
      setServerStorageLocations((prev) => ({
        ...prev,
        [applicationId]: response.data?.storageLocations || {}
      }));
    }
    if (response.data?.receiptPhotos) {
      setServerReceiptPhotos((prev) => ({
        ...prev,
        [applicationId]: response.data?.receiptPhotos || {}
      }));
    }
    if (options?.clearReminder) {
      onRefresh?.();
    }
    return true;
  };

  // 处理取件信息
  const handlePickupInfo = (consumable: any) => {
    setSelectedConsumable(consumable);
    setShowPickupModal(true);
    setPickupInfo({
      pickupLocation: '',
      pickupNumber: '',
      needsPickup: true
    });
  };

  // 提交取件信息
  const handleSubmitPickupInfo = () => {
    if (!selectedConsumable) return;

    // 应调用 API 向购买人发送取件通知

    // 更新状态为已到货
    updateProcurementStatus(selectedConsumable.applicationKey, '已到货', {
      pickupInfo,
      projectIndex: Number(selectedConsumable.projectIndex)
    });
    
    setShowPickupModal(false);
    alert('取件信息已发送通知给购买人');
  };

  // 处理放置位置 + 收货图并入库
  const handleStorageLocation = async (
    consumableKey: string,
    location: string,
    receiver?: string,
    receiptPhotoUrls: string[] = []
  ): Promise<boolean> => {
    if (!receiptPhotoUrls.length) {
      alert('请上传至少一张收货照片');
      return false;
    }
    setStorageLocation((prev) => ({
      ...prev,
      [consumableKey]: location
    }));

    const dash = consumableKey.lastIndexOf('-');
    const applicationKey = dash >= 0 ? consumableKey.slice(0, dash) : consumableKey;
    const projectIndex = Number(dash >= 0 ? consumableKey.slice(dash + 1) : 0);
    const stockInResponse = await stockInConsumableFromApproval(applicationKey, {
      projectIndex,
      location,
      ...(receiver ? { receiver } : {})
    });
    if (!stockInResponse.success) {
      alert(stockInResponse.error || '耗材落库失败');
      return false;
    }

    const ok = await updateProcurementStatus(applicationKey, '已入库', {
      projectIndex,
      storageLocation: location,
      receiptPhotoUrls
    });
    if (!ok) return false;

    window.dispatchEvent(new CustomEvent('consumableStockUpdated'));
    alert('已确认收货，耗材已入库');
    return true;
  };

  const procurementStatusPill = (status: string) => {
    const cls =
      status === '待采购'
        ? 'rm-status-pill rm-status-pill--pending'
        : status === '已采购'
          ? 'rm-status-pill rm-status-pill--ordered'
          : status === '已到货'
            ? 'rm-status-pill rm-status-pill--arrived'
            : status === '已入库'
              ? 'rm-status-pill rm-status-pill--stocked'
              : status === '不补货'
                ? 'rm-status-pill rm-status-pill--default'
                : status === '已处理'
                  ? 'rm-status-pill rm-status-pill--ordered'
              : 'rm-status-pill rm-status-pill--pending';
    return <span className={cls}>{status}</span>;
  };

  // 获取状态统计
  const getStatusStats = () => {
    const consumableApps = getConsumableApplications();
    const stats = {
      total: consumableApps.length,
      pending: 0,
      purchased: 0,
      arrived: 0,
      stocked: 0,
      totalAmount: 0
    };

    consumableApps.forEach(app => {
      const status = procurementStatus[app.key] || '待采购';
      stats.totalAmount += app.totalAmount || 0;
      
      switch (status) {
        case '待采购':
          stats.pending++;
          break;
        case '已采购':
          stats.purchased++;
          break;
        case '已到货':
          stats.arrived++;
          break;
        case '已入库':
          stats.stocked++;
          break;
        case '不补货':
          break;
      }
    });

    return stats;
  };

  const stats = getStatusStats();
  const consumableApps = getConsumableApplications();

  if (consumableApps.length === 0) {
    return (
      <div className="rm-procurement text-center py-5">
        <ShoppingCartOutlined style={{ fontSize: '64px', color: '#ccc' }} />
        <h4 className="text-muted mt-3">暂无待采购的耗材申请</h4>
        <p className="text-muted">
          {canManage
            ? '已通过审批的耗材申请将显示在这里'
            : '您暂无可查看的记录，或尚未有本人已通过审批的耗材申请。'}
        </p>
      </div>
    );
  }

  const filteredProcItems = getFilteredItems();
  const allFilteredSelected =
    filteredProcItems.length > 0 && filteredProcItems.every((i) => selectedItems.has(i.key));

  const statusPillOptions = ['全部', '待采购', '已处理', '已采购', '已到货', '已入库'] as const;
  const tableColSpan = canManage ? 14 : 13;

  const showReceiverForStorageModal = Boolean(
    pendingStorageApplicantId &&
      currentUserId &&
      String(pendingStorageApplicantId) !== String(currentUserId)
  );

  return (
    <div className="rm-procurement">
      {!canManage && (
        <p className="text-muted small mb-3 mb-md-2">
          以下为本人提交的采购进度。内部采购在「已到货」后填写放置位置入库；商家采购在「已采购」后可直接「确认收货」入库（无需「已到货」）。
        </p>
      )}
      <section className="mm-stats mm-stats--quad" aria-label="采购概览">
        <article className="mm-stat">
          <span className="mm-stat-label">总采购金额</span>
          <span className="mm-stat-value" style={{ fontVariantNumeric: 'tabular-nums' }}>
            ¥{stats.totalAmount.toFixed(2)}
          </span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="payments" sizePx={18} />
            <span>{canManage ? '已通过审批的申请合计' : '本人已通过审批的申请合计'}</span>
          </div>
          <LimsNavIcon name="account_balance" sizePx={120} className="mm-stat-deco" />
        </article>
        <article className="mm-stat">
          <span className="mm-stat-label">待采购</span>
          <span className={`mm-stat-value${stats.pending > 0 ? ' mm-stat-value--alert' : ''}`}>{stats.pending}</span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="schedule" sizePx={18} />
            <span>待执行条目</span>
          </div>
          <LimsNavIcon name="pending_actions" sizePx={120} className="mm-stat-deco" />
        </article>
        <article className="mm-stat">
          <span className="mm-stat-label">已采购 / 到货</span>
          <span className="mm-stat-value">{stats.purchased + stats.arrived}</span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="local_shipping" sizePx={18} />
            <span>物流与在途</span>
          </div>
          <LimsNavIcon name="inventory" sizePx={120} className="mm-stat-deco" />
        </article>
        <article className="mm-stat">
          <span className="mm-stat-label">已入库</span>
          <span className="mm-stat-value">{stats.stocked}</span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="warehouse" sizePx={18} />
            <span>完成入库</span>
          </div>
          <LimsNavIcon name="forklift" sizePx={120} className="mm-stat-deco" />
        </article>
      </section>

      <div className="mm-table-shell">
        <div className="mm-table-toolbar">
          <div className="d-flex flex-column gap-2 flex-grow-1 min-w-0">
            <div className="mm-filter-row flex-grow-1 flex-wrap">
              <span className="mm-filter-label">状态：</span>
              <div className="mm-filter-pills">
                {statusPillOptions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`mm-pill ${statusFilter === s ? 'is-active' : ''}`}
                    onClick={() => setStatusFilter(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="mm-filter-row flex-grow-1 flex-wrap">
              <span className="mm-filter-label">分类：</span>
              <div className="mm-filter-pills">
                <button
                  type="button"
                  className={`mm-pill ${categoryFilter === '全部' ? 'is-active' : ''}`}
                  onClick={() => setCategoryFilter('全部')}
                >
                  全部
                </button>
                {categoryOptions.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`mm-pill ${categoryFilter === c ? 'is-active' : ''}`}
                    onClick={() => setCategoryFilter(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="mm-toolbar-meta flex-wrap">
            <span className="me-2">
              <strong>{filteredProcItems.length}</strong> 条 · 申请 {stats.total}
            </span>
            {canManage && (
              <Button
                variant="outline-primary"
                size="sm"
                className="rounded-pill"
                onClick={handleExport}
                disabled={filteredProcItems.length === 0}
              >
                <DownloadOutlined /> 导出
              </Button>
            )}
          </div>
        </div>
        <div className="mm-sheet-scroll">
          <table className="mm-sheet">
            <thead>
              <tr>
                {canManage && (
                  <th style={{ width: 44 }}>
                    <Form.Check
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      aria-label="全选当前列表"
                    />
                  </th>
                )}
                <th>图片</th>
                <th>名称</th>
                <th>型号</th>
                <th>品牌</th>
                <th>单位</th>
                <th>单价</th>
                <th>采购总价</th>
                <th>平台下单</th>
                <th>申请人</th>
                <th>申请时间</th>
                <th>状态</th>
                <th>售后</th>
                <th className="mm-th-actions">操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredProcItems.length === 0 ? (
                <tr>
                  <td colSpan={tableColSpan} className="mm-empty">
                    当前筛选下暂无数据
                  </td>
                </tr>
              ) : (
                filteredProcItems.map((consumable) => {
                  const currentStatus = getProjectDisplayStatus(consumable.applicationKey, consumable.projectIndex);
                  const merchant = isMerchantPurchase(consumable);
                  const trackingText = formatMerchantTracking(consumable.merchantOrder);
                  const merchantNote = String(consumable.merchantOrder?.merchantFeedback || '').trim();
                  const platformNote = consumable.merchantOrder?.platformOrderNote;
                  const platformCompleted = merchant
                    ? Boolean(platformNote?.completed)
                    : Boolean(consumable.platformOrderRecord?.completed);
                  const canConfirmMerchantReceipt =
                    merchant &&
                    currentStatus === '已采购' &&
                    (canManage ||
                      String(consumable.applicantId || '') === String(currentUserId || ''));
                  return (
                  <tr key={consumable.key} className="mm-row">
                    {canManage && (
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedItems.has(consumable.key)}
                          onChange={(e) => handleSelectItem(consumable.key, e.target.checked)}
                          aria-label={`选择 ${consumable.name}`}
                        />
                      </td>
                    )}
                    <td>
                      <div className="rm-thumb-wrap">
                        <img
                          className="mm-instrument-thumb"
                          src={resolveUploadedImageUrl(consumable.image) || DEFAULT_CONSUMABLE_IMAGE}
                          alt=""
                        />
                        {consumable.autoRestock && (
                          <span className="rm-auto-restock-badge" title="自动补货提醒" aria-label="自动补货提醒">
                            <LimsNavIcon name="notifications_active" sizePx={13} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <p className="mm-identity-name mb-0">{consumable.name}</p>
                      {merchant && (
                        <div className="small text-muted mt-1">
                          商家采购
                          {consumable.merchantOrder?.merchantName
                            ? ` · ${consumable.merchantOrder.merchantName}`
                            : ''}
                        </div>
                      )}
                    </td>
                    <td>{consumable.model}</td>
                    <td>{consumable.brand || ''}</td>
                    <td>{consumable.unit}</td>
                    <td>{consumable.price ? `¥${consumable.price}` : '—'}</td>
                    <td>{formatLinePurchaseTotal(consumable)}</td>
                    <td>
                      {platformCompleted ? (
                        <Form.Check type="checkbox" checked disabled readOnly label="是" />
                      ) : canManage ? (
                        <Form.Check
                          type="checkbox"
                          checked={false}
                          label={
                            merchant
                              ? platformNote?.submittedAt
                                ? '待确认'
                                : '未完成'
                              : '未完成'
                          }
                          onChange={() =>
                            void handleConfirmPlatformOrder(consumable.applicationKey, {
                              isMerchant: merchant
                            })
                          }
                        />
                      ) : (
                        <span
                          className={
                            merchant && platformNote?.submittedAt ? 'text-warning' : 'text-muted'
                          }
                        >
                          {merchant && platformNote?.submittedAt ? '待确认' : '否'}
                        </span>
                      )}
                    </td>
                    <td>{consumable.applicant}</td>
                    <td>{new Date(consumable.applyTime).toLocaleDateString()}</td>
                    <td>
                      {procurementStatusPill(currentStatus)}
                      {merchant && currentStatus === '已采购' && (
                        <div className="small text-muted mt-1" style={{ maxWidth: 220 }}>
                          {trackingText ? (
                            <div>快递：{trackingText}</div>
                          ) : (
                            <div>待商家发货 / 填写运单</div>
                          )}
                          {merchantNote ? <div>备注：{merchantNote}</div> : null}
                        </div>
                      )}
                    </td>
                    <td>
                      <AfterSalesEntry
                        applicationId={String(consumable.applicationKey)}
                        projectIndex={Number(consumable.projectIndex)}
                        category="耗材申请"
                        itemName={String(consumable.name || '耗材')}
                        originalQuantity={Number(consumable.amount || 0)}
                        eligible={
                          merchant &&
                          consumable.merchantOrder?.orderStatus !== 'rejected'
                        }
                        isOwner={String(consumable.applicantId || '') === String(currentUserId || '')}
                        canManage={canManage}
                        requests={afterSalesRequests.filter(
                          (row) =>
                            String(row.applicationId) === String(consumable.applicationKey) &&
                            row.projectIndex === Number(consumable.projectIndex)
                        )}
                        onChanged={loadAfterSales}
                      />
                    </td>
                    <td>
                      <div className="mm-actions">
                        <button
                          type="button"
                          className="mm-icon-action"
                          title="查看"
                          onClick={() => {
                            setSelectedApplication(consumableApps.find((app) => app.key === consumable.applicationKey) || null);
                            setShowDetailModal(true);
                          }}
                        >
                          <LimsNavIcon name="visibility" sizePx={22} />
                        </button>
                        {canManage && currentStatus === '待采购' && consumable.autoRestock && (
                          <>
                            <button
                              type="button"
                              className="mm-icon-action"
                              title="去采购"
                              onClick={() => openGoPurchase(consumable)}
                            >
                              <LimsNavIcon name="shopping_cart" sizePx={22} />
                            </button>
                            <button
                              type="button"
                              className="mm-icon-action"
                              title="手动标记已处理"
                              onClick={() => {
                                setManualClearRow(consumable);
                                setManualClearShow(true);
                              }}
                            >
                              <LimsNavIcon name="task_alt" sizePx={22} />
                            </button>
                          </>
                        )}
                        {canManage && currentStatus === '已处理' && consumable.autoRestock && (
                          <>
                            <span className="small text-muted px-1" title="正式采购申请已通过，到货后将清除本提醒">
                              已处理·待到货
                            </span>
                            <button
                              type="button"
                              className="mm-icon-action"
                              title="手动清除提醒"
                              onClick={() => {
                                setManualClearRow(consumable);
                                setManualClearShow(true);
                              }}
                            >
                              <LimsNavIcon name="task_alt" sizePx={22} />
                            </button>
                          </>
                        )}
                        {canManage && currentStatus === '待采购' && !consumable.autoRestock && !merchant && (
                          <>
                            <button
                              type="button"
                              className="mm-icon-action"
                              title="编辑采购信息"
                              onClick={() => openEditProject(consumable)}
                            >
                              <LimsNavIcon name="edit_note" sizePx={22} />
                            </button>
                            <button
                              type="button"
                              className="mm-icon-action"
                              title="标记已采购"
                              onClick={() =>
                                updateProcurementStatus(consumable.applicationKey, '已采购', {
                                  projectIndex: Number(consumable.projectIndex)
                                })
                              }
                            >
                              <LimsNavIcon name="check_circle" sizePx={22} />
                            </button>
                          </>
                        )}
                        {canManage && currentStatus === '待采购' && !consumable.autoRestock && merchant && (
                          <span
                            className="small text-muted px-1"
                            title="等待商家接单后自动变为已采购"
                          >
                            {consumable.merchantOrder?.orderStatus === 'price_pending'
                              ? '历史改价待确认'
                              : '待商家接单'}
                          </span>
                        )}
                        {canManage && currentStatus === '已采购' && !merchant && (
                          <button
                            type="button"
                            className="mm-icon-action"
                            title="取件信息"
                            onClick={() => handlePickupInfo(consumable)}
                          >
                            <LimsNavIcon name="pin_drop" sizePx={22} />
                          </button>
                        )}
                        {canConfirmMerchantReceipt && (
                          <button
                            type="button"
                            className="mm-icon-action"
                            title="确认收货并入库"
                            onClick={() => {
                              setPendingStorageRowKey(`${consumable.applicationKey}-${consumable.projectIndex}`);
                              setPendingStorageApplicantId(String(consumable.applicantId || ''));
                              setPendingStorageItemLabel(String(consumable.name || ''));
                              setShowStorageLocationModal(true);
                            }}
                          >
                            <LimsNavIcon name="inventory_2" sizePx={22} />
                          </button>
                        )}
                        {currentStatus === '已到货' &&
                          !merchant &&
                          !storageLocation[`${consumable.applicationKey}-${consumable.projectIndex}`] && (
                            <button
                              type="button"
                              className="mm-icon-action"
                              title="填写放置位置"
                              onClick={() => {
                                setPendingStorageRowKey(`${consumable.applicationKey}-${consumable.projectIndex}`);
                                setPendingStorageApplicantId(String(consumable.applicantId || ''));
                                setPendingStorageItemLabel(String(consumable.name || ''));
                                setShowStorageLocationModal(true);
                              }}
                            >
                              <LimsNavIcon name="edit_location_alt" sizePx={22} />
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>编辑采购信息</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-3">
            可在采购前与申请人沟通后修改以下信息；修改将更新该申请项目，入库后则以「库存管理」为准。
          </div>
          {editForm && (
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>耗材分类</Form.Label>
                  <Form.Select
                    value={editForm.category === '公共耗材' || editForm.category === '小组耗材' ? editForm.category : '小组耗材'}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  >
                    <option value="公共耗材">公共耗材</option>
                    <option value="小组耗材">小组耗材</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>耗材名称</Form.Label>
                  <Form.Control
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>型号</Form.Label>
                  <Form.Control
                    value={editForm.model}
                    onChange={(e) => setEditForm({ ...editForm, model: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>品牌</Form.Label>
                  <Form.Select
                    value={editForm.brand}
                    onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })}
                  >
                    <option value="">请选择品牌</option>
                    {getConsumableBrandOptions(editForm.brand).map((brand) => (
                      <option key={brand} value={brand}>
                        {brand}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>单位</Form.Label>
                  <Form.Control
                    value={editForm.unit}
                    onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>购买渠道</Form.Label>
                  <Form.Select
                    value={editForm.purchaseChannel}
                    onChange={(e) => setEditForm({ ...editForm, purchaseChannel: e.target.value })}
                  >
                    <option value="">未选择</option>
                    <option value="网购">网购</option>
                    <option value="其他">其他</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {editForm.purchaseChannel === '网购' && (
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>购买链接</Form.Label>
                    <Form.Control
                      value={editForm.purchaseLink}
                      onChange={(e) => setEditForm({ ...editForm, purchaseLink: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              )}
              {editForm.purchaseChannel === '其他' && (
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>渠道商</Form.Label>
                    <Form.Control
                      value={editForm.supplier}
                      onChange={(e) => setEditForm({ ...editForm, supplier: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              )}
              <Col md={6}>
                <Form.Group>
                  <Form.Label>申请数量</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={editForm.amount}
                    onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>单价（元）</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step="0.01"
                    value={editForm.price}
                    onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>使用说明</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={editForm.usage}
                    onChange={(e) => setEditForm({ ...editForm, usage: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSaveEditProject} disabled={editSaving}>
            {editSaving ? '保存中...' : '保存'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 详情模态框 */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>耗材申请详情</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApplication && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>申请标题：</strong>
                  <span className="ms-2">{selectedApplication.title}</span>
                </Col>
                <Col md={6}>
                  <strong>申请人：</strong>
                  <span className="ms-2">{selectedApplication.applicant}</span>
                </Col>
              </Row>
              
              <Row className="mb-3">
                <Col md={6}>
                  <strong>申请时间：</strong>
                  <span className="ms-2">{new Date(selectedApplication.applyTime).toLocaleString()}</span>
                </Col>
                <Col md={6}>
                  <strong>总金额：</strong>
                  <span className="ms-2 text-primary fw-bold">¥{selectedApplication.totalAmount?.toFixed(2) || '0.00'}</span>
                </Col>
              </Row>

              <div className="mb-3">
                <h6>耗材项目详情</h6>
                {selectedApplication.consumableProjects?.map((project, index) => (
                  <Card key={index} className="mb-3" style={{ border: '1px solid #e9ecef' }}>
                    <Card.Header className="py-2">
                      <h6 className="mb-0">耗材项目 {index + 1}</h6>
                    </Card.Header>
                    <Card.Body className="py-3">
                      <Row className="g-3 align-items-start">
                        <Col xs={12} sm="auto">
                          <img
                            className="mm-instrument-thumb"
                            style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8 }}
                            src={resolveUploadedImageUrl(project.image) || DEFAULT_CONSUMABLE_IMAGE}
                            alt={project.name || '耗材'}
                          />
                        </Col>
                        <Col>
                          <Row>
                            <Col md={6}>
                              <strong>名称：</strong>
                              <span className="ms-2">{project.name}</span>
                            </Col>
                            <Col md={6}>
                              <strong>型号：</strong>
                              <span className="ms-2">{project.model}</span>
                            </Col>
                          </Row>
                          <Row className="mt-2">
                            <Col md={6}>
                              <strong>品牌：</strong>
                              <span className="ms-2">{project.brand || ''}</span>
                            </Col>
                            <Col md={6}>
                              <strong>数量：</strong>
                              <span className="ms-2">{project.amount}</span>
                            </Col>
                            <Col md={6}>
                              <strong>单价：</strong>
                              <span className="ms-2">¥{project.price}</span>
                            </Col>
                          </Row>
                          <Row className="mt-2">
                            <Col md={6}>
                              <strong>总价：</strong>
                              <span className="ms-2 text-primary fw-bold">¥{project.total}</span>
                            </Col>
                            <Col md={6}>
                              <strong>购买渠道：</strong>
                              <span className="ms-2">{project.purchaseChannel}</span>
                            </Col>
                          </Row>
                          {project.purchaseChannel === '网购' && project.purchaseLink && (
                            <Row className="mt-2">
                              <Col md={12}>
                                <strong>购买链接：</strong>
                                <span className="ms-2">
                                  <a href={project.purchaseLink} target="_blank" rel="noopener noreferrer">
                                    {project.purchaseLink}
                                  </a>
                                </span>
                              </Col>
                            </Row>
                          )}
                          {project.purchaseChannel === '其他' && project.supplier && (
                            <Row className="mt-2">
                              <Col md={12}>
                                <strong>渠道商：</strong>
                                <span className="ms-2">{project.supplier}</span>
                              </Col>
                            </Row>
                          )}
                          {(() => {
                            const appKey = selectedApplication.key;
                            const loc =
                              storageLocation[`${appKey}-${index}`] ||
                              serverStorageLocations[appKey]?.[String(index)] ||
                              '';
                            const photos = serverReceiptPhotos[appKey]?.[String(index)] || [];
                            if (!loc && photos.length === 0) return null;
                            return (
                              <Row className="mt-3">
                                <Col md={12}>
                                  <strong>确认收货信息</strong>
                                  {loc ? (
                                    <div className="mt-1">
                                      <span className="text-muted">放置位置：</span>
                                      {loc}
                                    </div>
                                  ) : null}
                                  {photos.length > 0 ? (
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                      {photos.map((url, pi) => (
                                        <a
                                          key={`${url}-${pi}`}
                                          href={resolveUploadedImageUrl(url) || url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          <img
                                            src={resolveUploadedImageUrl(url) || url}
                                            alt={`收货图 ${pi + 1}`}
                                            style={{
                                              width: 88,
                                              height: 88,
                                              objectFit: 'cover',
                                              borderRadius: 8,
                                              border: '1px solid #e9ecef'
                                            }}
                                          />
                                        </a>
                                      ))}
                                    </div>
                                  ) : null}
                                </Col>
                              </Row>
                            );
                          })()}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            关闭
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 取件信息模态框 */}
      <Modal show={showPickupModal} onHide={() => setShowPickupModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>取件信息</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedConsumable && (
            <div>
              <div className="mb-3">
                <h6>耗材信息</h6>
                <p><strong>名称：</strong>{selectedConsumable.name}</p>
                <p><strong>型号：</strong>{selectedConsumable.model}</p>
                <p><strong>申请人：</strong>{selectedConsumable.applicant}</p>
              </div>
              
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>是否需要取件</Form.Label>
                  <Form.Check
                    type="checkbox"
                    label="需要取件"
                    checked={pickupInfo.needsPickup}
                    onChange={(e) => setPickupInfo(prev => ({ ...prev, needsPickup: e.target.checked }))}
                  />
                </Form.Group>
                
                {pickupInfo.needsPickup && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>取件地点 *</Form.Label>
                      <Form.Control
                        type="text"
                        value={pickupInfo.pickupLocation}
                        onChange={(e) => setPickupInfo(prev => ({ ...prev, pickupLocation: e.target.value }))}
                        placeholder="请输入取件地点"
                        required
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>取件号</Form.Label>
                      <Form.Control
                        type="text"
                        value={pickupInfo.pickupNumber}
                        onChange={(e) => setPickupInfo(prev => ({ ...prev, pickupNumber: e.target.value }))}
                        placeholder="请输入取件号（可选）"
                      />
                    </Form.Group>
                  </>
                )}
              </Form>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPickupModal(false)}>
            取消
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmitPickupInfo}
            disabled={pickupInfo.needsPickup && !pickupInfo.pickupLocation}
          >
            确定
          </Button>
        </Modal.Footer>
      </Modal>

      <GoPurchaseFromRestockModal
        show={goPurchaseShow}
        onHide={() => {
          setGoPurchaseShow(false);
          setGoPurchasePrefill(null);
        }}
        prefillBase={goPurchasePrefill}
        itemLabel={goPurchaseLabel}
      />

      <ManualClearRestockModal
        show={manualClearShow}
        itemLabel={manualClearRow ? String(manualClearRow.name || '') : undefined}
        confirming={manualClearConfirming}
        onHide={() => {
          if (manualClearConfirming) return;
          setManualClearShow(false);
          setManualClearRow(null);
        }}
        onGoPurchase={() => {
          if (!manualClearRow) return;
          setManualClearShow(false);
          openGoPurchase(manualClearRow);
          setManualClearRow(null);
        }}
        onConfirmCleared={async () => {
          if (!manualClearRow?.applicationKey) return;
          setManualClearConfirming(true);
          try {
            const ok = await updateProcurementStatus(manualClearRow.applicationKey, '已处理', {
              clearReminder: true,
              projectIndex:
                manualClearRow.projectIndex != null ? Number(manualClearRow.projectIndex) : undefined
            });
            if (ok) {
              setManualClearShow(false);
              setManualClearRow(null);
              alert('已清除该低库存提醒');
            }
          } finally {
            setManualClearConfirming(false);
          }
        }}
      />

      <StorageLocationModal
        show={showStorageLocationModal}
        onHide={() => {
          setShowStorageLocationModal(false);
          setPendingStorageRowKey(null);
          setPendingStorageApplicantId('');
          setPendingStorageItemLabel('');
        }}
        resourceType="consumable"
        itemLabel={pendingStorageItemLabel || undefined}
        showReceiverInput={showReceiverForStorageModal}
        onConfirm={async (payload) => {
          if (!pendingStorageRowKey) return;
          const ok = await handleStorageLocation(
            pendingStorageRowKey,
            payload.location,
            payload.receiver,
            payload.receiptPhotoUrls
          );
          if (ok) {
            setShowStorageLocationModal(false);
            setPendingStorageRowKey(null);
            setPendingStorageApplicantId('');
            setPendingStorageItemLabel('');
          }
        }}
      />

      {/* 导出确认模态框 */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>导出确认</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            即将导出 <strong>{exportedItems.length}</strong> 个耗材项目
          </p>
          <p>导出格式：ZIP（含 CSV 清单 + 收货照片）</p>
          <p className="mb-0 text-muted small">已入库订单的收货图会一并打包进「收货照片」文件夹。</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={() => void exportToExcel()}>
            确认导出
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 状态更新确认模态框 */}
      <Modal show={showStatusUpdateModal} onHide={() => setShowStatusUpdateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>状态更新确认</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>您刚刚导出了 <strong>{exportedItems.length}</strong> 个"待采购"状态的耗材</p>
          <p>是否要将这些耗材的状态更新为"已采购"？</p>
          <div className="alert alert-info">
            <small>
              <strong>提示：</strong>更新后，这些耗材将显示"取件信息"按钮，可以进行后续的取件和入库操作。
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusUpdateModal(false)}>
            暂不更新
          </Button>
          <Button variant="primary" onClick={handleStatusUpdate}>
            确认更新
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ConsumableProcurement;
